import { Share2 } from "lucide-react";
import { useSettled } from "../../lib/queries";
import { fmtAmt, fmtUsd, fmtPrice, short, ago } from "../../lib/format";
import { useShare } from "./ShareProvider";

export const SettledTape = ({ onSelect, compact = false }) => {
  const { data, isLoading, isError, refetch } = useSettled();
  const openShare = useShare();
  const rows = compact ? (data || []).slice(0, 8) : data || [];
  return (
    <section className={`panel flex flex-col min-h-0 ${compact ? "" : "flex-1 max-lg:flex-none max-lg:h-[70vh]"}`} data-testid="settled-tape-container">
      <div className="panel-head">
        <div className="flex items-center gap-3"><h2 className="panel-title">Trade History</h2><span className="mono text-[11px] text-fog">public feed · {data?.length ?? 0} trades</span></div>
        <span className="flex items-center gap-1.5 mono text-[10px] text-fog"><span className="w-1.5 h-1.5 rounded-full bg-pos animate-pulseDot" /> live</span>
      </div>
      <div className="overflow-auto flex-1">
        <table className={`w-full text-xs ${compact ? "" : "min-w-[820px]"}`}>
          <thead className="sticky top-0 bg-s1"><tr className="eyebrow text-left [&>th]:px-3 [&>th]:h-8 [&>th]:font-normal border-b border-line">
            <th>Time</th><th>Token · pair</th><th className="text-right">Size</th><th className="text-right">Price · USD/M</th><th className="text-right">Notional</th><th className="text-right">Impact saved</th><th className="text-right">Fee · $CROSS</th>{!compact && <><th>Seller → Buyer</th><th>Tx</th></>}<th className="text-right">Share</th>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={10} className="p-6 text-center text-fog">Loading history…</td></tr>}
            {isError && !isLoading && (
              <tr><td colSpan={10} className="p-8 text-center" data-testid="settled-tape-error">
                <div className="text-neg font-medium">Couldn't load trade history.</div>
                <div className="text-slate-400 text-xs mt-1">The backend didn't respond.</div>
                <button onClick={() => refetch()} className="btn-ghost h-8 px-3 text-xs mt-3" data-testid="settled-tape-retry-btn">Retry</button>
              </td></tr>
            )}
            {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-400" data-testid="settled-tape-empty">No settled deals yet. The first settlement will appear here with token, size, price and timestamp.</td></tr>}
            {!isError && rows.map((d, i) => (
              <tr key={d.id} onClick={() => onSelect?.(d.id)} data-testid={`tape-row-${d.id}`} className={`row-hover border-b border-line/60 mono [&>td]:px-3 [&>td]:h-9 ${i === 0 ? "animate-flash" : ""}`}>
                <td className="text-fog">{ago(d.settled_at)}<div className="text-[10px] text-slate-600">{new Date(d.settled_at).toISOString().slice(11, 19)}Z</div></td>
                <td className="font-heading font-semibold text-white">{d.token_symbol}<span className="text-fog font-normal font-mono text-[10px]">/{d.stock}</span></td>
                <td className="text-right">{fmtAmt(d.token_amount)}</td>
                <td className="text-right">{fmtPrice(d.price_usd)}</td>
                <td className="text-right text-slate-100">{fmtUsd(d.notional_usd)}</td>
                <td className={`text-right font-medium ${d.slippage.saved_usd >= 0 ? "text-violet-300" : "text-neg"}`}>{d.slippage.saved_usd >= 0 ? "+" : ""}{fmtUsd(d.slippage.saved_usd)}</td>
                <td className="text-right text-slate-300">{fmtAmt(d.fee_cross)}</td>
                {!compact && <><td className="text-fog">{d.private ? <span className="text-violet-300">Private → Private</span> : <>{short(d.seller_address)} → {short(d.buyer_address)}</>}</td><td className="text-slate-600">{d.settle_tx?.slice(0, 12)}…</td></>}
                <td className="text-right">
                  <button onClick={(e) => { e.stopPropagation(); openShare(d); }} title="Share this settled deal"
                    className="inline-flex items-center justify-center h-6 w-6 rounded border border-line2 text-fog hover:text-violet-300 hover:border-violet transition-colors" data-testid={`share-deal-${d.id}`}>
                    <Share2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
